import { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { getClientId } from "../../api/realtimeClientId.js";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

const RealtimeContext = createContext(null);

export function useRealtime() {
  return useContext(RealtimeContext);
}

function buildWsUrl() {
  const url = new URL("/api/casual/realtime", API_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("clientId", getClientId());
  return url.toString();
}

// Sumontuojamas VIENĄ kartą CasualHome lygmenyje (ne kiekviename view atskirai),
// kad ryšys išliktų naršant tarp Kainininko/Duomenų matricos, ir prenumeratoriai
// (EditableDataTable, KainininkasMasterView, DuomenuMatricaView) gautų gyvus
// entity/presence įvykius per bendrą subscribe().
export function RealtimeProvider({ children }) {
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const attemptRef = useRef(0);
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;

    function connect() {
      const socket = new WebSocket(buildWsUrl());
      socketRef.current = socket;

      socket.onopen = () => {
        attemptRef.current = 0;
      };
      socket.onmessage = (event) => {
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
        listenersRef.current.forEach((fn) => fn(msg));
      };
      socket.onclose = () => {
        if (closedRef.current) return;
        const delay = Math.min(1000 * 2 ** attemptRef.current, 15000) + Math.random() * 500;
        attemptRef.current += 1;
        setTimeout(() => {
          if (!closedRef.current) connect();
        }, delay);
      };
      socket.onerror = () => socket.close();
    }

    connect();
    return () => {
      closedRef.current = true;
      socketRef.current?.close();
    };
  }, []);

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const sendPresence = useCallback((msg) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ ...msg, type: "presence" }));
    }
  }, []);

  const value = { clientId: getClientId(), subscribe, sendPresence };
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
